// Dynamic wizard step configuration.
//
// The Agricola policy wizard renders a different set of steps depending on the
// product selected in the holder step. Each flow is an ordered list of step
// keys; the wizard maps the active position to its component (by key, not by a
// fixed index) and only the steps in the active flow are enabled/validated.

export type WizardStepKey =
  | 'holder'
  | 'declaratoria'
  | 'premium'
  | 'beneficiaries'
  | 'dependants'
  | 'payment';

export interface WizardStepDefinition {
  key: WizardStepKey;
  /** Label shown in the progress UI. */
  label: string;
  /** Top-level reactive form group backing this step. */
  group: string;
}

export const WIZARD_STEP_DEFINITIONS: Record<WizardStepKey, WizardStepDefinition> = {
  holder: { key: 'holder', label: 'Datos principales', group: 'step1' },
  declaratoria: { key: 'declaratoria', label: 'Declaratoria', group: 'step2' },
  premium: { key: 'premium', label: 'Cálculo de prima', group: 'step3' },
  beneficiaries: { key: 'beneficiaries', label: 'Beneficiarios', group: 'step4' },
  dependants: { key: 'dependants', label: 'Dependientes', group: 'step4b' },
  payment: { key: 'payment', label: 'Pago y apertura de póliza', group: 'step5' }
};

/** Assistance products: holder → premium → dependants → payment. */
export const ASSISTANCE_STEP_FLOW: WizardStepKey[] = ['holder', 'premium', 'dependants', 'payment'];

/**
 * Assistance products that do not opt into dependants (no `dependents: true` in
 * their plan/product config) skip that step: holder → premium → payment.
 */
export const ASSISTANCE_HOLDER_ONLY_STEP_FLOW: WizardStepKey[] = ['holder', 'premium', 'payment'];

/** Default flow for non-assistance products: holder → premium → payment. */
export const PRODUCT_STEP_FLOW: WizardStepKey[] = ['holder', 'premium', 'payment'];

/**
 * Per-product overrides keyed by internal product code, for products that need
 * a flow different from {@link PRODUCT_STEP_FLOW}. Anything not listed here
 * falls back to the full legacy flow via {@link FALLBACK_STEP_FLOW}.
 */
export const PRODUCT_STEP_FLOWS: Record<string, WizardStepKey[]> = {
  AGRICOLA_FRE_CYBER: PRODUCT_STEP_FLOW,
  AGRICOLA_FRE_CYBER_MUVIT: PRODUCT_STEP_FLOW
};

/** Legacy six-step flow, used when a product has no configured flow. */
export const FALLBACK_STEP_FLOW: WizardStepKey[] = [
  'holder',
  'declaratoria',
  'premium',
  'beneficiaries',
  'dependants',
  'payment'
];

export interface WizardFlowOptions {
  /**
   * Whether the selected assistance admits dependants. Resolved at runtime from
   * the plan/product config (see AgricolaService.allowsDependants); defaults to
   * true so a product with no such config keeps the full assistance flow.
   */
  allowsDependants?: boolean;
}

export function resolveWizardStepKeys(
  productCode: string,
  isAssistance: boolean,
  options?: WizardFlowOptions
): WizardStepKey[] {
  if (isAssistance) {
    return options?.allowsDependants === false ? ASSISTANCE_HOLDER_ONLY_STEP_FLOW : ASSISTANCE_STEP_FLOW;
  }

  return PRODUCT_STEP_FLOWS[productCode] ?? FALLBACK_STEP_FLOW;
}

export function resolveWizardSteps(
  productCode: string,
  isAssistance: boolean,
  options?: WizardFlowOptions
): WizardStepDefinition[] {
  return resolveWizardStepKeys(productCode, isAssistance, options).map(key => WIZARD_STEP_DEFINITIONS[key]);
}

/** Top-level form groups that exist but may be inactive for a given flow. */
export const ALL_STEP_GROUPS: string[] = Object.values(WIZARD_STEP_DEFINITIONS).map(step => step.group);
